import tokens from '@kluein/design-tokens';
import { Modal } from '@kluein/klue-ui';
import { X } from '@phosphor-icons/react';
import { Box, Layer } from 'grommet';

import { appUIContext, useAppUIProvider } from './appUIContext';

import type { ToastItem } from './appUIContext';

type AppProps = {
  children: React.ReactNode;
};

// Simple inline toast component for stacking (doesn't use Layer per toast)
function StackedToast({
  toast,
  onClose,
}: {
  toast: ToastItem;
  onClose: () => void;
}) {
  const { title, message, action } = toast.props;

  return (
    <Box
      direction="column"
      elevation="medium"
      pad="small"
      background="white"
      width={{ max: '400px' }}
      style={{ position: 'relative' }}
    >
      <X
        style={{
          cursor: 'pointer',
          position: 'absolute',
          right: 12,
          top: 12,
        }}
        size={16}
        onClick={onClose}
      />
      <Box gap="xsmall" pad={{ right: 'large' }}>
        {title && (
          <Box>
            <strong
              style={{ fontSize: '14px', color: tokens.color.neutral[900] }}
            >
              {title}
            </strong>
          </Box>
        )}
        {message && (
          <Box>
            <span
              style={{ fontSize: '14px', color: tokens.color.neutral[900] }}
            >
              {message}
            </span>
          </Box>
        )}
        {action && <Box flex="grow">{action}</Box>}
      </Box>
    </Box>
  );
}

export function AppUIProvider(props: AppProps) {
  const uiState = useAppUIProvider();

  return (
    <>
      {/* Render stacked toasts in a single Layer */}
      {uiState.toast.toasts.length > 0 && (
        <Layer
          position="bottom-left"
          modal={false}
          margin={{ vertical: 'small', horizontal: 'small' }}
          responsive={false}
          plain
        >
          <Box direction="column" gap="small">
            {uiState.toast.toasts.map((toast) => (
              <StackedToast
                key={toast.id}
                toast={toast}
                onClose={() => uiState.toast.close(toast.id)}
              />
            ))}
          </Box>
        </Layer>
      )}

      {uiState.modal.isOpen && (
        <Modal {...uiState.modal.props} onClose={uiState.modal.close} />
      )}

      <appUIContext.Provider value={uiState}>
        {props.children}
      </appUIContext.Provider>
    </>
  );
}
